import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  inject,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs/operators';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { DemoDataService } from 'src/shared/services/demo-data.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';
import { MarketingWifiService, WifiTableRow } from 'src/shared/services/marketing-wifi.service';
import { WifiRouterStatusPage } from '../wifi-router-status/wifi-router-status.page';
import { HardwareInfoPage } from 'src/pages/compliance/hardware-info/hardware-info.page';
import { OrderSensorComponent } from 'src/shared/components/order-sensor/order-sensor.component';
import { SensorPromoCardComponent } from 'src/shared/components/sensor-promo-card/sensor-promo-card.component';

@Component({
  selector: 'wifi-tab',
  templateUrl: './wifi-tab.component.html',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TidyUiComponentsModule, SharedModule, SensorPromoCardComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class WifiTabComponent implements OnInit, OnDestroy {
  private readonly wifiService = inject(MarketingWifiService);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly windowService = inject(WindowService);
  private readonly aboutThisPageService = inject(AboutThisPageService);
  private readonly demoDataService = inject(DemoDataService);
  private readonly demoBlock = inject(DemoBlockService);

  isDemo = false;

  loaded = false;
  headers = ['Property', 'Access Points', 'Wi-Fi Status', 'last-column'];
  allRows: WifiTableRow[] = [];
  rows: WifiTableRow[] = [];
  searchForm = this.fb.group({ search: [''] });
  showAboutThisPage = false;
  overlayTop: string;

  private _wifiHeader: ElementRef;
  private overlayResizeObserver: ResizeObserver;
  @ViewChild('wifiHeader', { read: ElementRef }) set wifiHeader(el: ElementRef) {
    this._wifiHeader = el;
    if (el && this.showAboutThisPage && !this.overlayResizeObserver) {
      this.setupOverlayObserver();
    }
  }

  ngOnInit() {
    this.searchForm.get('search')!.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      this.rows = this.wifiService.filterRows(this.allRows, value || '');
      this.cdr.markForCheck();
    });

    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('wifi-marketing');
    this.isDemo = this.demoDataService.isDemoAccount();
    if (this.showAboutThisPage || this.isDemo) {
      this.loadMockData();
      return;
    }

    this.allRows = [];
    this.rows = [];
    this.loaded = true;
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this.overlayResizeObserver?.disconnect();
  }

  private loadMockData() {
    this.headers = ['Property', 'Access Points', 'Wi-Fi Status', 'last-column'];
    const mockProperties = [
      { name: 'Beach House', color: '#4CAF50' },
      { name: 'Mountain Cabin', color: '#2196F3' },
      { name: 'City Apartment', color: '#FF9800' },
      { name: 'Lake House', color: '#9C27B0' },
      { name: 'Downtown Loft', color: '#F44336' },
      { name: 'Desert Retreat', color: '#E91E63' },
    ];
    const mockStatuses: Array<{ label: string; icon: string }> = [
      { label: 'Online', icon: 'assets/img/checkmark-circle-green.svg' },
      { label: 'Online', icon: 'assets/img/checkmark-circle-green.svg' },
      { label: 'Degraded', icon: 'assets/img/yellow-alert.svg' },
      { label: 'Online', icon: 'assets/img/checkmark-circle-green.svg' },
      { label: 'Offline', icon: 'assets/img/close-circle-red.svg' },
      { label: 'Online', icon: 'assets/img/checkmark-circle-green.svg' },
    ];
    const mockAPs = ['2 APs', '1 AP', '1 AP', '3 APs', '1 AP', '2 APs'];
    const mockRows: WifiTableRow[] = [];
    for (let i = 0; i < 12; i++) {
      const property = mockProperties[i % mockProperties.length];
      const status = mockStatuses[i % mockStatuses.length];
      const aps = mockAPs[i % mockAPs.length];
      mockRows.push({
        id: i + 1,
        'Property': { value: property.name, action: null, color: property.color, addressId: i + 1 },
        'Access Points': { value: aps, action: null },
        'Wi-Fi Status': { value: status.label, action: null, icon: status.icon, showIconOnRight: true as const },
        'last-column': { value: i + 1, action: null, hasChevron: true as const },
      });
    }
    this.allRows = mockRows;
    this.rows = [...mockRows];
    this.loaded = true;
    this.cdr.markForCheck();
  }

  private setupOverlayObserver() {
    const updateTop = () => {
      if (!this._wifiHeader) {return;}
      const top = this._wifiHeader.nativeElement.getBoundingClientRect().top - 8;
      this.overlayTop = `${top  }px`;
      this.cdr.markForCheck();
    };
    this.overlayResizeObserver = new ResizeObserver(() => updateTop());
    this.overlayResizeObserver.observe(document.body);
    setTimeout(() => updateTop());
  }

  dismissAboutThisPage() {
    this.showAboutThisPage = false;
    this.overlayResizeObserver?.disconnect();
    this.aboutThisPageService.dismissAboutThisPage('wifi-marketing');
    this.rows = [];
    this.allRows = [];
    this.cdr.markForCheck();
  }

  openHardwareInfo() {
    if (this.showAboutThisPage || this.isDemo) {return;}
    if (this.windowService.isDesktopRes) {
      this.storage.save('dialog-right-side-open', true);
      this.storage.save('dialogParams', { hardwareType: 'wifi' });
      this.rightSidePanelService.openRightSidePanel(HardwareInfoPage);
    } else {
      this.navCtrl.navigateForward('hardware-info', { hardwareType: 'wifi' });
    }
  }

  openOrderSensor() {
    if (this.showAboutThisPage || this.isDemo) {return;}
    if (this.demoBlock.block()) { return; }
    if (this.windowService.isDesktopRes) {
      this.storage.save('dialog-right-side-open', true);
      this.storage.save('dialogParams', { sourceContext: 'wifi' });
      this.rightSidePanelService.openRightSidePanel(OrderSensorComponent);
    } else {
      this.navCtrl.navigateForward('order-sensor', { sourceContext: 'wifi' });
    }
  }

  onTableRowClick(event: { column: string; element: any }) {
    if (this.showAboutThisPage || this.isDemo) {return;}
    if (this.demoBlock.block()) { return; }
    const propertyId = event.element['Property']?.addressId;
    const wifiData = this.wifiService.getRouterStatus(propertyId);
    if (!wifiData) { return; }

    if (this.windowService.isDesktopRes) {
      this.storage.save('dialog-right-side-open', true);
      this.storage.save('dialogParams', { wifiData });
      this.rightSidePanelService.openRightSidePanel(WifiRouterStatusPage);
    } else {
      this.navCtrl.navigateForward('wifi-router-status', { wifiData });
    }
  }
}
